import React from 'react';

import * as yup from 'yup';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams, useNavigate } from 'react-router-dom';

import defaultClient from '@src/api/clients/default';
import { FormBase, Submit, TextField } from '@src/components/form';
import { useMessage } from '@src/context';
import useForm from '@src/hooks/useForm';
import leadGurusLogo from '@src/assets/logo-full-dark.png';

const schema = yup.object().shape({
  new_password1: yup.string().min(8, 'Use at least 8 characters').required('Password is required'),
  new_password2: yup
    .string()
    .oneOf([yup.ref('new_password1'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

export default function AccountSetup() {
  const [params] = useSearchParams();
  const message = useMessage();
  const navigate = useNavigate();

  const uid = params.get('uid');
  const token = params.get('token');

  const form = useForm({
    schema,
    defaultValues: { new_password1: '', new_password2: '' },
    resetAfterSuccessSubmit: true,
  });

  const [done, setDone] = React.useState(false);
  const [linkExpired, setLinkExpired] = React.useState(false);
  // idle | sending | sent | already_active
  const [resendState, setResendState] = React.useState('idle');

  const handleResend = () => {
    setResendState('sending');
    defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/setup-account/resend/',
        method: 'POST',
        payload: { uid },
      })
      .then((response) => {
        setResendState(response?.data?.code === 'already_active' ? 'already_active' : 'sent');
      })
      .catch((error) => {
        setResendState('idle');
        const detail = error?.response?.data?.detail;
        message.error(detail || 'Unable to send a new link. Please contact your Lead Gurus account team.');
      });
  };

  if (!uid || !token) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>Invalid Setup Link</Typography>
            <Typography color="text.secondary">
              This link is invalid or has expired. Please check your email for a valid invite link.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (linkExpired) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            {resendState === 'sent' ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Fresh Link Sent</Typography>
                <Typography color="text.secondary">
                  We just emailed you a new setup link. Open the most recent email and click the
                  button there to finish creating your account.
                </Typography>
              </>
            ) : resendState === 'already_active' ? (
              <>
                <Typography variant="h6" gutterBottom>Account Already Set Up</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  This account already has a password. Sign in below, or use &ldquo;Forgot
                  password?&rdquo; on the login page if you need a new one.
                </Typography>
                <Submit
                  variant="contained"
                  onClick={() => navigate('/auth/login')}
                  sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}
                >
                  Sign In
                </Submit>
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>This Link Has Expired</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  No problem — we can email you a fresh setup link right now.
                </Typography>
                <Submit
                  variant="contained"
                  disabled={resendState === 'sending'}
                  onClick={handleResend}
                  sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}
                >
                  {resendState === 'sending' ? 'Sending…' : 'Email me a fresh link'}
                </Submit>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Account Created!</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your password has been set. You can now sign in to the Lead Gurus portal.
            </Typography>
            <Submit variant="contained" onClick={() => navigate('/auth/login')} sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}>
              Sign In
            </Submit>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleSubmit = (payload) =>
    defaultClient
      .request({
        auth: false,
        url: 'accounts/auth/password/reset/confirm/',
        method: 'POST',
        payload: { ...payload, uid, token },
      })
      .then(() => {
        setDone(true);
      })
      .catch((error) => {
        const data = error?.response?.data;
        if (data && (data.token || data.uid)) {
          setLinkExpired(true);
          return;
        }
        const passwordErrors = data?.new_password1 || data?.new_password2;
        if (Array.isArray(passwordErrors) && passwordErrors.length) {
          message.error(passwordErrors[0]);
          return;
        }
        message.error('Unable to create your account. The link may be invalid or expired.');
      });

  return (
    <Box sx={{ maxWidth: 480, width: '100%' }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28, marginBottom: 16 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>Set Up Your Account</Typography>
            <Typography color="text.secondary">
              Create a password to access your Lead Gurus client portal.
            </Typography>
          </Box>
          <FormBase onSubmit={form.handleSubmit({ onSubmit: handleSubmit })}>
            <Stack spacing={2.5}>
              <TextField
                control={form.control}
                fullWidth
                label="Create password"
                name="new_password1"
                type="password"
                autoComplete="new-password"
              />
              <TextField
                control={form.control}
                fullWidth
                label="Confirm password"
                name="new_password2"
                type="password"
                autoComplete="new-password"
              />
              <Submit
                variant="contained"
                fullWidth
                sx={{ py: 1.5, bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' }, fontWeight: 600 }}
              >
                Create Account
              </Submit>
            </Stack>
          </FormBase>
        </CardContent>
      </Card>
    </Box>
  );
}

AccountSetup.displayName = 'AccountSetup';
